// Pure step-navigation guards for ContractCreateWizard.
// Extracted so vitest can drive the truth tables without
// mounting the React component. The wizard imports these and
// keeps state in React; all field shape decisions live here.

import type { DeploymentReason, PricingScheme } from '@bhops/shared';

export type WizardStep = 'header' | 'lines' | 'pricing';

export interface DraftHeader {
  dealId: string;
  startDate: string;
  endDate: string;
  durationMonths: string;
  renewalScheduledAt: string;
}

export interface DraftLine {
  modelId: string;
  quantity: string;
  reason: DeploymentReason;
  unitPriceMonthly: string;
  currency: string;
  effectiveFrom: string;
  notes: string;
}

export interface DraftPricing {
  scheme: PricingScheme;
  totalMonthly: string;
  totalOneTime: string;
  deposit: string;
  currency: string;
  exchangeRateSnapshot: string;
  effectiveFrom: string;
  paymentSchedule: string;
}

export function canAdvanceHeader(h: DraftHeader): boolean {
  if (!h.dealId || !h.startDate || !h.endDate || !h.durationMonths) return false;
  // Mirror of server-side `endDate > startDate` CHECK.
  return new Date(h.endDate) > new Date(h.startDate);
}

export function canAdvanceLines(lines: DraftLine[]): boolean {
  if (lines.length === 0) return false;
  return lines.every(
    (l) =>
      Boolean(l.modelId) &&
      Boolean(l.quantity) &&
      Number(l.quantity) >= 1 &&
      Boolean(l.unitPriceMonthly) &&
      l.currency.length === 3 &&
      Boolean(l.effectiveFrom),
  );
}

/**
 * Backwards navigation table — `pricing` ↔ `lines` ↔ `header`.
 * Returns the previous step, or null if already at the first.
 */
export function previousStep(step: WizardStep): WizardStep | null {
  if (step === 'pricing') return 'lines';
  if (step === 'lines') return 'header';
  return null;
}

/**
 * Forward step transition — encodes the "jump-to-step disabled
 * when prerequisite incomplete" rule from design §7.1 case 3.
 *
 * Returns the next step on success, or `null` (with the failed
 * step preserved) when the precondition isn't met.
 */
export function tryAdvance(
  step: WizardStep,
  header: DraftHeader,
  lines: DraftLine[],
): WizardStep | null {
  if (step === 'header') {
    return canAdvanceHeader(header) ? 'lines' : null;
  }
  if (step === 'lines') {
    return canAdvanceLines(lines) ? 'pricing' : null;
  }
  // pricing is the terminal step — no forward transition.
  return null;
}
